import { useSelector, useDispatch } from 'react-redux';
import { useState, useMemo, useEffect } from 'react';
import {
  updateConfigurationValue,
  updateConfigurationValueQuickRun,
  updateTriggerTestStatus,
} from '../../store/actions/onboarding';
import {
  getFrameworkConfigurationQuickRun,
  getTestRunnerCmd,
  getShellOption,
} from '../../utils/onboarding/languageBasedConfig';
import { getFrameworkConfigurationLinuxQuickRun } from '../../utils/onboarding/languageBasedConfigLinux';
import axios from 'axios';
import yaml from 'js-yaml';
import { getHypertestConcurrency } from 'store/actions/concurrency';
import quickRunConcurrencyHandler from 'utils/onboarding/quickRunConcurrencyHandler';
import { abortJob } from 'store/actions/hypertestJobList';
import { sendAmplitudeEvents } from 'utils/common';
import { EventNames } from 'utils/events';
const queryString = require('query-string');

let MAX_CONCURRENCY = 25;
const MIN_CONCURRENCY = 1;
const MAX_RETRY = 10;
const MIN_RETRY = 1;
const MAX_TIMEOUT = 120;
const MIN_TIMEOUT = 1;

const useQuickRunConfiguration = () => {
  const dispatch = useDispatch();
  const [currentOsSelection, setCurrentOsSelection] = useState(0);
  const [showGoToDashBoard, setShowGoToDashBoard] = useState(false);
  const userInfo = useSelector((state) => state.userInfo?.userInfo);
  const userConcurrency = useSelector(
    (state) => state.concurrency?.concurrency?.data?.data?.available
  );
  const currentConfiguration = useSelector(
    (state) => state.onboarding?.currentConfigurationQuickRun
  );
  const auth = useMemo(() => {
    return {
      username: userInfo?.data.username,
      password: userInfo?.data.apiToken,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.data?.username, userInfo?.data?.apiToken]);

  let parsed = queryString.parse(window.location.search);

  useEffect(() => {
    resetTestStatus();
    let platform = 'win';
    if (window.navigator.platform.indexOf('Win') !== -1) {
      platform = 'win';
    } else if (window.navigator.platform.indexOf('Mac') !== -1) {
      platform = 'mac';
    } else if (window.navigator.platform.indexOf('Linux') !== -1) {
      platform = 'linux';
    }
    dispatch(
      updateConfigurationValue({
        conciergePlatform: platform,
      })
    );
    dispatch(
      getHypertestConcurrency({
        auth: {
          username: userInfo?.data.username,
          password: userInfo?.data.apiToken,
        },
        orgId: userInfo?.data?.organization?.id,
      })
    );
    let urlSegments = window.location.pathname.split('/');
    dispatch(
      updateConfigurationValue({
        framework: {
          ...currentConfiguration.framework,
          name: urlSegments[urlSegments.length - 1],
        },
        frameworkType: parsed && parsed.frameworkType,
        testType: 'quick-run',
      })
    );
    dispatch(
      updateConfigurationValueQuickRun({
        framework: {
          ...currentConfiguration.framework,
          name: urlSegments[urlSegments.length - 1],
        },
        frameworkType: parsed && parsed.frameworkType,
        testType: 'quick-run',
        platform: 'win',
      })
    );
    let langConfig = getFrameworkConfigurationQuickRun(
      urlSegments[urlSegments.length - 1],
      parsed && parsed.frameworkType
    );
    let testRunnerCmd = getTestRunnerCmd(
      platform,
      urlSegments[urlSegments.length - 1],
      parsed && parsed.frameworkType
    );
    let concurrency = 1;
    let retry = 1;
    // if (parsed && parsed.frameworkType === 'PlayWright') {
    //   concurrency = 1;
    // }
    dispatch(
      updateConfigurationValueQuickRun({
        frameworkConfiguration: langConfig,
        testRunner: testRunnerCmd ? testRunnerCmd : langConfig?.testRunnerCmd,
        testdiscovery: langConfig?.testDiscoveryCmd,
        concurrency: concurrency,
        conciergePlatform: platform,
        retry: retry,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let currentFramework = currentConfiguration.frameworkConfiguration;

  function getYamlCode() {
    let currentFrameWorkName = currentConfiguration?.framework?.name;
    let framework = getFrameworkConfigurationQuickRun(
      currentFrameWorkName,
      currentConfiguration.frameworkType
    );
    let yamlCode = framework.yamlCode;

    if (currentConfiguration.platform === 'linux') {
      yamlCode = getFrameworkConfigurationLinuxQuickRun(
        currentFrameWorkName,
        currentConfiguration.frameworkType
      ).yamlCode;
    }
    let shellOption = getShellOption(
      currentConfiguration.platform,
      currentFrameWorkName
    );
    let testRunnerCmd = getTestRunnerCmd(
      currentConfiguration.platform,
      currentFrameWorkName,
      currentConfiguration.frameworkType
    );
    let mapObj = {
      CONCURRENCY: currentConfiguration.concurrency,
      MAXRETRIES: currentConfiguration.retry,
      TIMEOUT: currentConfiguration.timeout,
      PLATFORM: currentConfiguration.platform,
      TEST_RUNNER: testRunnerCmd,
      SHELL_OPTION: shellOption,
    };
    yamlCode = yamlCode?.replace(
      /PLATFORM|CONCURRENCY|MAXRETRIES|TIMEOUT|TEST_DISCOVERY|TEST_RUNNER|SHELL_OPTION|RETRYONFAILURE/g,
      function (matched) {
        return mapObj[matched];
      }
    );
    return yamlCode;
  }

  function setPlatform(type) {
    dispatch(
      updateConfigurationValueQuickRun({
        platform: type,
      })
    );
  }

  function handleOsChange(index) {
    setCurrentOsSelection(index);
    let os = 'win';
    if (index === 0) {
      os = 'win';
    } else if (index === 1) {
      os = 'linux';
    }
    sendAmplitudeEvents(EventNames.HYP_QUICK_RUN_UPDATE_YAML, {
      parameter: 'os',
      os: os,
    });
    setPlatform(os);
  }

  function updateYaml(yamlCode) {
    dispatch(
      updateConfigurationValueQuickRun({
        frameworkConfiguration: {
          ...currentConfiguration.frameworkConfiguration,
          yamlCode: yamlCode,
        },
      })
    );
  }

  function handleMaxMin(value, maxValue, minValue) {
    let output = value;
    if (value > maxValue) {
      output = maxValue;
    } else if (value < minValue) {
      value = minValue;
    }
    return output;
  }

  function changeTimeoutValue(value = 1) {
    value = handleMaxMin(parseInt(value || 0), MAX_TIMEOUT, MIN_TIMEOUT);
    sendAmplitudeEvents(EventNames.HYP_QUICK_RUN_UPDATE_YAML, {
      parameter: 'timeout',
      timeout: value,
    });
    dispatch(
      updateConfigurationValueQuickRun({
        timeout: value,
      })
    );
  }

  function changeRetryValue(value = 0) {
    value = handleMaxMin(parseInt(value || 0), MAX_RETRY, MIN_RETRY);
    sendAmplitudeEvents(EventNames.HYP_QUICK_RUN_UPDATE_YAML, {
      parameter: 'retry',
      retry: value,
    });
    dispatch(
      updateConfigurationValueQuickRun({
        retry: value,
      })
    );
  }

  function changeConcurrencyValue(value = 0) {
    if (quickRunConcurrencyHandler() > userConcurrency) {
      MAX_CONCURRENCY = userConcurrency;
    } else {
      MAX_CONCURRENCY = quickRunConcurrencyHandler();
    }

    value = handleMaxMin(
      parseInt(value || 0),
      MAX_CONCURRENCY,
      MIN_CONCURRENCY
    );
    sendAmplitudeEvents(EventNames.HYP_QUICK_RUN_UPDATE_YAML, {
      parameter: 'concurrency',
      concurrency: value,
    });
    dispatch(
      updateConfigurationValueQuickRun({
        concurrency: value,
      })
    );
  }

  function getCommandToRunTest() {
    let command = currentFramework.runTestCommand;
    let conciergeBasedOnOs = './hyperexecute';
    switch (currentConfiguration.conciergePlatform) {
      case 'win':
        conciergeBasedOnOs = 'hyperexecute.exe';
        break;
      case 'mac':
      case 'linux':
        conciergeBasedOnOs = './hyperexecute';
        break;
      default:
        break;
    }

    let config = 'hyperexecute.yaml';

    let mapObj = {
      USERNAME: userInfo && userInfo.data && userInfo.data.username,
      ACCESSKEY: userInfo && userInfo.data && userInfo.data.apiToken,
      CONCIERGE: conciergeBasedOnOs,
      CONFIG: config,
    };
    command = command.replace(
      /USERNAME|ACCESSKEY|CONCIERGE|CONFIG/g,
      function (matched) {
        return mapObj[matched];
      }
    );
    return command;
  }

  function triggerButtonHandler() {
    const headers = {
      Accept: 'application/json',
      Authorization:
        'Basic ' +
        Buffer.from(auth.username + ':' + auth.password).toString('base64'),
    };
    headers['Content-Type'] = 'application/json';
    const data = yaml.load(getYamlCode());
    const url =
      process.env.REACT_APP_HYPERTEST_ORCHESTRATOR_API_ENDPOINT +
      '/v1.0/trigger-job';
    let requestOptions = {
      url: url,
      method: 'POST',
      headers,
      data: data,
    };
    axios
      .request(requestOptions)
      .then((res) => {
        const jobId = res?.data?.data?.jobID;
        window.quickRunData = {
          currentStep: 1,
          logs: '',
          jobId,
        };
        dispatch(
          updateTriggerTestStatus({
            triggerTestStatus: {
              currentStep: 1,
              logs: '',
            },
          })
        );
        dispatch(
          updateConfigurationValueQuickRun({
            jobId: jobId,
          })
        );
      })
      .catch((err) => {
        console.log(err);
      });
    setShowGoToDashBoard(true);
  }
  const handleJobAbort = async (jobId) => {
    dispatch(
      abortJob({
        auth: {
          username: userInfo.data && userInfo.data.username,
          password: userInfo.data && userInfo.data.apiToken,
        },
        jobId,
      })
    );
  };

  function handleRetriggerJob() {
    handleJobAbort(window?.quickRunData?.jobId);
    triggerButtonHandler();
  }

  function resetTestStatus() {
    window.quickRunData = {
      currentStep: 0,
      logs: '',
      jobId: '',
    };
    dispatch(
      updateTriggerTestStatus({
        triggerTestStatus: {
          currentStep: 0,
          logs: '',
        },
      })
    );
  }

  return {
    changeConcurrencyValue,
    changeRetryValue,
    changeTimeoutValue,
    currentConfiguration,
    currentFramework,
    currentOsSelection,
    getCommandToRunTest,
    getYamlCode,
    handleOsChange,
    resetTestStatus,
    showGoToDashBoard,
    triggerButtonHandler,
    updateYaml,
    handleRetriggerJob,
  };
};

export default useQuickRunConfiguration;
